import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Tab, Tabs } from 'react-bootstrap';
import apiClient, { API_ENDPOINTS } from '../utils/api';

/**
 * サイト投稿（tnewt.com へのコンテンツ公開）。
 *
 * 保存すると管理システムが GitHub にコミットし、Amplify の自動ビルドを経て
 * 3〜5分で本番サイトに反映される。即時には反映されない点を画面上でも明示している。
 */

const CATEGORIES = ['お知らせ', '民泊・観光', 'IT・テクノロジー'];
const FACILITY_CATEGORIES = [
  { value: 'restaurant', label: '飲食店' },
  { value: 'cafe', label: 'カフェ' },
  { value: 'activity', label: 'アクティビティ' },
  { value: 'shopping', label: 'ショッピング' },
  { value: 'other', label: 'その他' },
];

const today = () => new Date().toISOString().slice(0, 10);

const emptyPost = () => ({
  slug: '',
  title: '',
  description: '',
  date: today(),
  category: 'お知らせ',
  thumbnail: '',
  author: '',
  keywords: '',
  draft: false,
  body: '',
});

const emptyFacility = () => ({
  id: null,
  name: '',
  category: 'restaurant',
  description: '',
  image: '',
  images: [],
  stockPhoto: false,
  distance: '',
  address: '',
  tel: '',
  openingHours: { time: '', holidays: '' },
  services: [],
  benefits: [],
  highlights: [],
});

const emptyProperty = () => ({
  slug: '',
  name: '',
  catchphrase: '',
  description: '',
  body: '',
  address: '',
  access: '',
  capacity: 0,
  bedrooms: null,
  checkIn: '15:00',
  checkOut: '11:00',
  priceFrom: null,
  thumbnail: '',
  images: [],
  amenities: [],
  bookingLinks: [],
  published: true,
  order: 999,
});

// 「A, B, C」形式の入力欄と配列を相互変換する
const toLines = (arr) => (arr || []).join('\n');
const fromLines = (text) =>
  (text || '')
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

const errorMessage = (e, fallback) =>
  e?.response?.data?.detail || e?.message || fallback;

/** 画像を1枚アップロードして、サイト上のパス(/image/cms/xxx.webp)を返すボタン */
function ImageUploadButton({ label = '画像をアップロード', onUploaded, disabled }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const inputId = `upload-${Math.random().toString(36).slice(2)}`;

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setBusy(true);
    setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.post(API_ENDPOINTS.CMS_IMAGES, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
      });
      onUploaded(res.data.path);
    } catch (err) {
      setError(errorMessage(err, '画像のアップロードに失敗しました。'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Form.Control id={inputId} type="file" accept="image/*" className="d-none" onChange={handleChange} />
      <Button
        as="label"
        htmlFor={inputId}
        variant="outline-secondary"
        size="sm"
        disabled={disabled || busy}
        className="mb-0"
      >
        {busy ? 'アップロード中...' : label}
      </Button>
      {error && <div className="text-danger small mt-1">{error}</div>}
    </>
  );
}

function SitePosts() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const [posts, setPosts] = useState([]);
  const [postForm, setPostForm] = useState(emptyPost());
  const [postEditing, setPostEditing] = useState(null);

  const [facilities, setFacilities] = useState([]);
  const [facilityForm, setFacilityForm] = useState(emptyFacility());

  const [properties, setProperties] = useState([]);
  const [propertyForm, setPropertyForm] = useState(emptyProperty());
  const [propertyEditing, setPropertyEditing] = useState(null);

  const [rooms, setRooms] = useState(null);
  const [amenitiesJson, setAmenitiesJson] = useState('');

  const notify = (text) => {
    setMessage(text);
    setError('');
  };
  const fail = (e, fallback) => {
    setError(errorMessage(e, fallback));
    setMessage('');
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const statusRes = await apiClient.get(API_ENDPOINTS.CMS_STATUS);
      setStatus(statusRes.data);
      if (!statusRes.data.configured) {
        setLoading(false);
        return;
      }
      const [postsRes, facilitiesRes, propertiesRes, roomsRes] = await Promise.all([
        apiClient.get(API_ENDPOINTS.CMS_POSTS),
        apiClient.get(API_ENDPOINTS.CMS_FACILITIES),
        apiClient.get(API_ENDPOINTS.CMS_PROPERTIES),
        apiClient.get(API_ENDPOINTS.CMS_ROOMS),
      ]);
      setPosts(postsRes.data || []);
      setFacilities(facilitiesRes.data || []);
      setProperties(propertiesRes.data || []);
      setRooms(roomsRes.data || null);
      setAmenitiesJson(JSON.stringify(roomsRes.data?.amenities ?? {}, null, 2));
    } catch (e) {
      fail(e, 'サイトのコンテンツを取得できませんでした。');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const canPublish = status?.enabled;

  // ------------------------------------------------------ ブログ・お知らせ

  const editPost = async (slug) => {
    try {
      const res = await apiClient.get(`${API_ENDPOINTS.CMS_POSTS}/${slug}`);
      setPostForm({ ...emptyPost(), ...res.data });
      setPostEditing(slug);
      notify(`「${res.data.title}」を編集中です。`);
    } catch (e) {
      fail(e, '記事の取得に失敗しました。');
    }
  };

  const savePost = async () => {
    if (!postForm.title.trim()) {
      fail(null, 'タイトルを入力してください。');
      return;
    }
    setBusy(true);
    try {
      const res = postEditing
        ? await apiClient.put(`${API_ENDPOINTS.CMS_POSTS}/${postEditing}`, postForm)
        : await apiClient.post(API_ENDPOINTS.CMS_POSTS, postForm);
      notify(
        `保存しました。ビルド完了後（3〜5分）に ${res.data.url} で公開されます。`,
      );
      setPostForm(emptyPost());
      setPostEditing(null);
      const list = await apiClient.get(API_ENDPOINTS.CMS_POSTS);
      setPosts(list.data || []);
    } catch (e) {
      fail(e, '記事の保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  const removePost = async (slug) => {
    if (!window.confirm(`記事「${slug}」をサイトから削除します。よろしいですか？`)) return;
    setBusy(true);
    try {
      await apiClient.delete(`${API_ENDPOINTS.CMS_POSTS}/${slug}`);
      setPosts((prev) => prev.filter((p) => p.slug !== slug));
      notify('削除しました。ビルド完了後にサイトから消えます。');
    } catch (e) {
      fail(e, '記事の削除に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- 周辺施設

  const editFacility = (facility) => {
    setFacilityForm({ ...emptyFacility(), ...facility });
    notify(`「${facility.name}」を編集中です。`);
  };

  const saveFacility = async () => {
    if (!facilityForm.name.trim()) {
      fail(null, '施設名を入力してください。');
      return;
    }
    setBusy(true);
    try {
      if (facilityForm.id) {
        await apiClient.put(`${API_ENDPOINTS.CMS_FACILITIES}/${facilityForm.id}`, facilityForm);
      } else {
        await apiClient.post(API_ENDPOINTS.CMS_FACILITIES, facilityForm);
      }
      const list = await apiClient.get(API_ENDPOINTS.CMS_FACILITIES);
      setFacilities(list.data || []);
      setFacilityForm(emptyFacility());
      notify('保存しました。ビルド完了後（3〜5分）に周辺施設ページへ反映されます。');
    } catch (e) {
      fail(e, '施設の保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  const removeFacility = async (facility) => {
    if (!window.confirm(`「${facility.name}」を削除します。よろしいですか？`)) return;
    setBusy(true);
    try {
      await apiClient.delete(`${API_ENDPOINTS.CMS_FACILITIES}/${facility.id}`);
      setFacilities((prev) => prev.filter((f) => f.id !== facility.id));
      notify('削除しました。');
    } catch (e) {
      fail(e, '施設の削除に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  // -------------------------------------------------------------------- 物件

  const editProperty = (property) => {
    setPropertyForm({ ...emptyProperty(), ...property });
    setPropertyEditing(property.slug);
    notify(`「${property.name}」を編集中です。`);
  };

  const saveProperty = async () => {
    if (!propertyForm.name.trim()) {
      fail(null, '物件名を入力してください。');
      return;
    }
    setBusy(true);
    try {
      const res = propertyEditing
        ? await apiClient.put(`${API_ENDPOINTS.CMS_PROPERTIES}/${propertyEditing}`, propertyForm)
        : await apiClient.post(API_ENDPOINTS.CMS_PROPERTIES, propertyForm);
      const list = await apiClient.get(API_ENDPOINTS.CMS_PROPERTIES);
      setProperties(list.data || []);
      setPropertyForm(emptyProperty());
      setPropertyEditing(null);
      notify(`保存しました。ビルド完了後（3〜5分）に ${res.data.url} で公開されます。`);
    } catch (e) {
      fail(e, '物件の保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  const removeProperty = async (property) => {
    if (!window.confirm(`「${property.name}」を削除します。よろしいですか？`)) return;
    setBusy(true);
    try {
      await apiClient.delete(`${API_ENDPOINTS.CMS_PROPERTIES}/${property.slug}`);
      setProperties((prev) => prev.filter((p) => p.slug !== property.slug));
      notify('削除しました。');
    } catch (e) {
      fail(e, '物件の削除に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- 部屋紹介

  const updateRooms = (patch) => setRooms((prev) => ({ ...prev, ...patch }));

  const updateFloor = (index, patch) => {
    setRooms((prev) => {
      const floors = [...(prev.floors || [])];
      floors[index] = { ...floors[index], ...patch };
      return { ...prev, floors };
    });
  };

  const saveRooms = async () => {
    setBusy(true);
    try {
      let amenities;
      try {
        amenities = JSON.parse(amenitiesJson);
      } catch (parseError) {
        fail(null, '設備・アメニティのJSONが不正です。括弧やカンマを確認してください。');
        setBusy(false);
        return;
      }
      const payload = { ...rooms, amenities };
      await apiClient.put(API_ENDPOINTS.CMS_ROOMS, payload);
      setRooms(payload);
      notify('保存しました。ビルド完了後（3〜5分）に部屋紹介ページへ反映されます。');
    } catch (e) {
      fail(e, '部屋紹介の保存に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  // ---------------------------------------------------------------- 表示

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="mb-0">サイト投稿</h2>
        <Button variant="outline-secondary" size="sm" onClick={loadAll} disabled={busy}>
          再読み込み
        </Button>
      </div>

      <Alert variant="info" className="py-2">
        保存すると <strong>tnewt.com のリポジトリにコミット</strong> され、自動ビルドを経て
        <strong>約3〜5分後</strong>にサイトへ反映されます。保存直後にサイトを見ても、まだ古い内容が表示されます。
      </Alert>

      {!status?.configured && (
        <Alert variant="warning">
          GitHub 連携が未設定のため投稿できません。Cloud Run の環境変数に <code>GITHUB_TOKEN</code>（contents:write
          権限の fine-grained PAT）と <code>GITHUB_REPO</code> を設定してください。
        </Alert>
      )}
      {status?.configured && !status?.enabled && (
        <Alert variant="warning">
          デモモード（またはモック有効）のため、サイトへの公開は無効化されています。
        </Alert>
      )}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <Tabs defaultActiveKey="posts" className="mb-3">
        {/* ---------------------------------------------- ブログ・お知らせ */}
        <Tab eventKey="posts" title={`ブログ・お知らせ (${posts.length})`}>
          <Row>
            <Col lg={5} className="mb-4">
              <Card>
                <Card.Header>投稿済みの記事</Card.Header>
                <Card.Body className="p-0">
                  {posts.length === 0 && <div className="p-3 text-muted">まだ記事がありません。</div>}
                  {posts.map((post) => (
                    <div key={post.slug} className="border-bottom p-3">
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <div className="fw-bold">{post.title}</div>
                          <div className="small text-muted">
                            {post.date} ／ {post.category} ／ {post.slug}
                            {post.draft && <Badge bg="secondary" className="ms-2">下書き</Badge>}
                          </div>
                        </div>
                        <div className="text-nowrap">
                          <Button size="sm" variant="outline-primary" onClick={() => editPost(post.slug)}>
                            編集
                          </Button>{' '}
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() => removePost(post.slug)}
                            disabled={!canPublish || busy}
                          >
                            削除
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={7}>
              <Card>
                <Card.Header>{postEditing ? `記事を編集：${postEditing}` : '新しい記事を書く'}</Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>タイトル</Form.Label>
                    <Form.Control
                      value={postForm.title}
                      onChange={(e) => setPostForm({ ...postForm, title: e.target.value })}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>slug（URL・英数字）</Form.Label>
                        <Form.Control
                          value={postForm.slug}
                          placeholder="awaji-summer-info"
                          disabled={!!postEditing}
                          onChange={(e) => setPostForm({ ...postForm, slug: e.target.value })}
                        />
                        <Form.Text muted>tnewt.com/blog/〈slug〉 になります。日本語タイトルのときは必須。</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>公開日</Form.Label>
                        <Form.Control
                          type="date"
                          value={postForm.date}
                          onChange={(e) => setPostForm({ ...postForm, date: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>カテゴリ</Form.Label>
                        <Form.Select
                          value={postForm.category}
                          onChange={(e) => setPostForm({ ...postForm, category: e.target.value })}
                        >
                          {CATEGORIES.map((c) => (
                            <option key={c} value={c}>{c}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>説明（一覧・検索結果に出る要約）</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={postForm.description}
                      onChange={(e) => setPostForm({ ...postForm, description: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>検索キーワード（カンマ区切り・SEO用）</Form.Label>
                    <Form.Control
                      value={postForm.keywords || ''}
                      placeholder="淡路島 グルメ, 淡路島 玉ねぎ, 淡路牛"
                      onChange={(e) => setPostForm({ ...postForm, keywords: e.target.value })}
                    />
                    <Form.Text muted>
                      検索されたい語をそのまま並べます。meta keywords と構造化データに使われます。
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>サムネイル画像</Form.Label>
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Control
                        value={postForm.thumbnail || ''}
                        placeholder="/image/cms/xxx.webp"
                        onChange={(e) => setPostForm({ ...postForm, thumbnail: e.target.value })}
                      />
                      <ImageUploadButton
                        disabled={!canPublish}
                        onUploaded={(path) => setPostForm((prev) => ({ ...prev, thumbnail: path }))}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>本文（Markdown）</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={14}
                      value={postForm.body}
                      placeholder={'## 見出し\n\n本文を書きます。\n\n- 箇条書き\n- 箇条書き\n\n## Q. よくある質問は？\n\nA. この形式で書くと検索結果のFAQに載ります。'}
                      onChange={(e) => setPostForm({ ...postForm, body: e.target.value })}
                    />
                    <Form.Text muted>
                      「## Q. 質問」→「A. 回答」の形式で書いた箇所は、検索エンジン・生成AI向けのFAQ構造化データとして自動的に出力されます。
                    </Form.Text>
                  </Form.Group>

                  <Form.Check
                    className="mb-3"
                    type="checkbox"
                    label="下書き（サイトには表示しない）"
                    checked={postForm.draft}
                    onChange={(e) => setPostForm({ ...postForm, draft: e.target.checked })}
                  />

                  <div className="d-flex gap-2">
                    <Button onClick={savePost} disabled={!canPublish || busy}>
                      {postEditing ? '更新して公開' : '公開する'}
                    </Button>
                    {postEditing && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setPostForm(emptyPost());
                          setPostEditing(null);
                        }}
                      >
                        新規作成に戻す
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* ------------------------------------------------------ 周辺施設 */}
        <Tab eventKey="facilities" title={`周辺施設 (${facilities.length})`}>
          <Row>
            <Col lg={5} className="mb-4">
              <Card>
                <Card.Header>掲載中の施設</Card.Header>
                <Card.Body className="p-0">
                  {facilities.map((facility) => (
                    <div key={facility.id} className="border-bottom p-3 d-flex justify-content-between gap-2">
                      <div>
                        <div className="fw-bold">{facility.name}</div>
                        <div className="small text-muted">
                          {FACILITY_CATEGORIES.find((c) => c.value === facility.category)?.label || facility.category}
                          ／{facility.distance}
                          {facility.stockPhoto && <Badge bg="secondary" className="ms-2">イメージ写真</Badge>}
                        </div>
                      </div>
                      <div className="text-nowrap">
                        <Button size="sm" variant="outline-primary" onClick={() => editFacility(facility)}>編集</Button>{' '}
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => removeFacility(facility)}
                          disabled={!canPublish || busy}
                        >
                          削除
                        </Button>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={7}>
              <Card>
                <Card.Header>{facilityForm.id ? `施設を編集：${facilityForm.name}` : '施設を追加'}</Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={7}>
                      <Form.Group className="mb-3">
                        <Form.Label>施設名</Form.Label>
                        <Form.Control
                          value={facilityForm.name}
                          onChange={(e) => setFacilityForm({ ...facilityForm, name: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>カテゴリ</Form.Label>
                        <Form.Select
                          value={facilityForm.category}
                          onChange={(e) => setFacilityForm({ ...facilityForm, category: e.target.value })}
                        >
                          {FACILITY_CATEGORIES.map((c) => (
                            <option key={c.value} value={c.value}>{c.label}</option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>紹介文</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={facilityForm.description}
                      onChange={(e) => setFacilityForm({ ...facilityForm, description: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>メイン写真</Form.Label>
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Control
                        value={facilityForm.image}
                        placeholder="/image/cms/xxx.webp"
                        onChange={(e) => setFacilityForm({ ...facilityForm, image: e.target.value })}
                      />
                      <ImageUploadButton
                        disabled={!canPublish}
                        onUploaded={(path) => setFacilityForm((prev) => ({ ...prev, image: path }))}
                      />
                    </div>
                  </Form.Group>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>当宿からの距離</Form.Label>
                        <Form.Control
                          value={facilityForm.distance}
                          placeholder="車で10分"
                          onChange={(e) => setFacilityForm({ ...facilityForm, distance: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>住所</Form.Label>
                        <Form.Control
                          value={facilityForm.address}
                          onChange={(e) => setFacilityForm({ ...facilityForm, address: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>電話番号</Form.Label>
                        <Form.Control
                          value={facilityForm.tel}
                          onChange={(e) => setFacilityForm({ ...facilityForm, tel: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>営業時間</Form.Label>
                        <Form.Control
                          value={facilityForm.openingHours?.time || ''}
                          placeholder="11:00 - 21:00"
                          onChange={(e) =>
                            setFacilityForm({
                              ...facilityForm,
                              openingHours: { ...facilityForm.openingHours, time: e.target.value },
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>定休日</Form.Label>
                        <Form.Control
                          value={facilityForm.openingHours?.holidays || ''}
                          placeholder="定休日：木曜日"
                          onChange={(e) =>
                            setFacilityForm({
                              ...facilityForm,
                              openingHours: { ...facilityForm.openingHours, holidays: e.target.value },
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>設備・サービス（1行に1つ）</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={toLines(facilityForm.services)}
                      placeholder={'駐車場あり\n個室あり'}
                      onChange={(e) => setFacilityForm({ ...facilityForm, services: fromLines(e.target.value) })}
                    />
                  </Form.Group>

                  <Form.Check
                    className="mb-3"
                    type="checkbox"
                    label="掲載写真はイメージ写真（実店舗の写真ではない）"
                    checked={facilityForm.stockPhoto}
                    onChange={(e) => setFacilityForm({ ...facilityForm, stockPhoto: e.target.checked })}
                  />

                  <div className="d-flex gap-2">
                    <Button onClick={saveFacility} disabled={!canPublish || busy}>
                      {facilityForm.id ? '更新して公開' : '追加して公開'}
                    </Button>
                    {facilityForm.id && (
                      <Button variant="outline-secondary" onClick={() => setFacilityForm(emptyFacility())}>
                        新規追加に戻す
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* ---------------------------------------------------------- 物件 */}
        <Tab eventKey="properties" title={`民泊物件 (${properties.length})`}>
          <Row>
            <Col lg={5} className="mb-4">
              <Card>
                <Card.Header>掲載中の物件</Card.Header>
                <Card.Body className="p-0">
                  {properties.length === 0 && <div className="p-3 text-muted">まだ物件がありません。</div>}
                  {properties.map((property) => (
                    <div key={property.slug} className="border-bottom p-3 d-flex justify-content-between gap-2">
                      <div>
                        <div className="fw-bold">{property.name}</div>
                        <div className="small text-muted">
                          {property.slug}／最大{property.capacity}名
                          {property.published === false && <Badge bg="secondary" className="ms-2">非公開</Badge>}
                        </div>
                      </div>
                      <div className="text-nowrap">
                        <Button size="sm" variant="outline-primary" onClick={() => editProperty(property)}>編集</Button>{' '}
                        <Button
                          size="sm"
                          variant="outline-danger"
                          onClick={() => removeProperty(property)}
                          disabled={!canPublish || busy}
                        >
                          削除
                        </Button>
                      </div>
                    </div>
                  ))}
                </Card.Body>
              </Card>
            </Col>

            <Col lg={7}>
              <Card>
                <Card.Header>{propertyEditing ? `物件を編集：${propertyEditing}` : '物件を追加'}</Card.Header>
                <Card.Body>
                  <Row>
                    <Col md={7}>
                      <Form.Group className="mb-3">
                        <Form.Label>物件名</Form.Label>
                        <Form.Control
                          value={propertyForm.name}
                          onChange={(e) => setPropertyForm({ ...propertyForm, name: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={5}>
                      <Form.Group className="mb-3">
                        <Form.Label>slug（URL・英数字）</Form.Label>
                        <Form.Control
                          value={propertyForm.slug}
                          placeholder="awaji-myojin"
                          disabled={!!propertyEditing}
                          onChange={(e) => setPropertyForm({ ...propertyForm, slug: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>キャッチコピー</Form.Label>
                    <Form.Control
                      value={propertyForm.catchphrase}
                      placeholder="海まで徒歩30秒、一棟貸切のプライベートヴィラ"
                      onChange={(e) => setPropertyForm({ ...propertyForm, catchphrase: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>概要（一覧カードに出る文章）</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={propertyForm.description}
                      onChange={(e) => setPropertyForm({ ...propertyForm, description: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>詳細本文（空行で段落が分かれます）</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={8}
                      value={propertyForm.body}
                      onChange={(e) => setPropertyForm({ ...propertyForm, body: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>メイン写真</Form.Label>
                    <div className="d-flex gap-2 align-items-center">
                      <Form.Control
                        value={propertyForm.thumbnail}
                        placeholder="/image/cms/xxx.webp"
                        onChange={(e) => setPropertyForm({ ...propertyForm, thumbnail: e.target.value })}
                      />
                      <ImageUploadButton
                        disabled={!canPublish}
                        onUploaded={(path) => setPropertyForm((prev) => ({ ...prev, thumbnail: path }))}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>写真ギャラリー（1行に1つ、画像パス）</Form.Label>
                    <div className="d-flex gap-2 align-items-start">
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={toLines((propertyForm.images || []).map((i) => i.src))}
                        onChange={(e) =>
                          setPropertyForm({
                            ...propertyForm,
                            images: fromLines(e.target.value).map((src) => ({
                              src,
                              alt: propertyForm.name || '物件写真',
                            })),
                          })
                        }
                      />
                      <ImageUploadButton
                        disabled={!canPublish}
                        label="追加"
                        onUploaded={(path) =>
                          setPropertyForm((prev) => ({
                            ...prev,
                            images: [...(prev.images || []), { src: path, alt: prev.name || '物件写真' }],
                          }))
                        }
                      />
                    </div>
                  </Form.Group>

                  <Row>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>定員</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          value={propertyForm.capacity}
                          onChange={(e) =>
                            setPropertyForm({ ...propertyForm, capacity: Number(e.target.value) || 0 })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>部屋数</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          value={propertyForm.bedrooms ?? ''}
                          onChange={(e) =>
                            setPropertyForm({
                              ...propertyForm,
                              bedrooms: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>チェックイン</Form.Label>
                        <Form.Control
                          value={propertyForm.checkIn || ''}
                          onChange={(e) => setPropertyForm({ ...propertyForm, checkIn: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group className="mb-3">
                        <Form.Label>チェックアウト</Form.Label>
                        <Form.Control
                          value={propertyForm.checkOut || ''}
                          onChange={(e) => setPropertyForm({ ...propertyForm, checkOut: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={8}>
                      <Form.Group className="mb-3">
                        <Form.Label>住所</Form.Label>
                        <Form.Control
                          value={propertyForm.address}
                          onChange={(e) => setPropertyForm({ ...propertyForm, address: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>最低料金（円／泊）</Form.Label>
                        <Form.Control
                          type="number"
                          min={0}
                          value={propertyForm.priceFrom ?? ''}
                          onChange={(e) =>
                            setPropertyForm({
                              ...propertyForm,
                              priceFrom: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>アクセス補足</Form.Label>
                    <Form.Control
                      value={propertyForm.access}
                      placeholder="北淡ICから車で約10分／無料駐車場あり"
                      onChange={(e) => setPropertyForm({ ...propertyForm, access: e.target.value })}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>設備・アメニティ（1行に1つ）</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={toLines(propertyForm.amenities)}
                      onChange={(e) => setPropertyForm({ ...propertyForm, amenities: fromLines(e.target.value) })}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Check
                        className="mb-3"
                        type="checkbox"
                        label="サイトに公開する"
                        checked={propertyForm.published}
                        onChange={(e) => setPropertyForm({ ...propertyForm, published: e.target.checked })}
                      />
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>並び順（小さいほど先頭）</Form.Label>
                        <Form.Control
                          type="number"
                          value={propertyForm.order}
                          onChange={(e) =>
                            setPropertyForm({ ...propertyForm, order: Number(e.target.value) || 999 })
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex gap-2">
                    <Button onClick={saveProperty} disabled={!canPublish || busy}>
                      {propertyEditing ? '更新して公開' : '追加して公開'}
                    </Button>
                    {propertyEditing && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setPropertyForm(emptyProperty());
                          setPropertyEditing(null);
                        }}
                      >
                        新規追加に戻す
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Tab>

        {/* ------------------------------------------------------ 部屋紹介 */}
        <Tab eventKey="rooms" title="部屋紹介ページ">
          {!rooms ? (
            <Alert variant="secondary">部屋紹介データを取得できませんでした。</Alert>
          ) : (
            <Card>
              <Card.Header>tnewt.com/minpaku/rooms の内容</Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group className="mb-3">
                      <Form.Label>小見出し（英字）</Form.Label>
                      <Form.Control
                        value={rooms.intro?.eyebrow || ''}
                        onChange={(e) => updateRooms({ intro: { ...rooms.intro, eyebrow: e.target.value } })}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={8}>
                    <Form.Group className="mb-3">
                      <Form.Label>見出し</Form.Label>
                      <Form.Control
                        value={rooms.intro?.heading || ''}
                        onChange={(e) => updateRooms({ intro: { ...rooms.intro, heading: e.target.value } })}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>紹介文（改行はそのまま反映されます）</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    value={rooms.intro?.body || ''}
                    onChange={(e) => updateRooms({ intro: { ...rooms.intro, body: e.target.value } })}
                  />
                </Form.Group>

                <Form.Group className="mb-4">
                  <Form.Label>トップのスライダー写真（1行に1つ）</Form.Label>
                  <div className="d-flex gap-2 align-items-start">
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={toLines((rooms.sliderImages || []).map((i) => i.src))}
                      onChange={(e) =>
                        updateRooms({
                          sliderImages: fromLines(e.target.value).map((src, i) => ({
                            src,
                            alt: rooms.sliderImages?.[i]?.alt || `室内の様子 ${i + 1}`,
                          })),
                        })
                      }
                    />
                    <ImageUploadButton
                      disabled={!canPublish}
                      label="追加"
                      onUploaded={(path) =>
                        updateRooms({
                          sliderImages: [
                            ...(rooms.sliderImages || []),
                            { src: path, alt: `室内の様子 ${(rooms.sliderImages || []).length + 1}` },
                          ],
                        })
                      }
                    />
                  </div>
                </Form.Group>

                {(rooms.floors || []).map((floor, index) => (
                  <Card key={floor.badge || index} className="mb-3">
                    <Card.Header className="py-2">{floor.badge} セクション</Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={3}>
                          <Form.Group className="mb-3">
                            <Form.Label>階の表示</Form.Label>
                            <Form.Control
                              value={floor.badge || ''}
                              onChange={(e) => updateFloor(index, { badge: e.target.value })}
                            />
                          </Form.Group>
                        </Col>
                        <Col md={9}>
                          <Form.Group className="mb-3">
                            <Form.Label>間取りの説明</Form.Label>
                            <Form.Control
                              value={floor.rooms || ''}
                              onChange={(e) => updateFloor(index, { rooms: e.target.value })}
                            />
                          </Form.Group>
                        </Col>
                      </Row>

                      <Form.Group className="mb-3">
                        <Form.Label>見出し（改行可）</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={2}
                          value={floor.heading || ''}
                          onChange={(e) => updateFloor(index, { heading: e.target.value })}
                        />
                      </Form.Group>

                      <Form.Group className="mb-3">
                        <Form.Label>写真（1行に1つ）</Form.Label>
                        <div className="d-flex gap-2 align-items-start">
                          <Form.Control
                            as="textarea"
                            rows={3}
                            value={toLines(floor.images)}
                            onChange={(e) => updateFloor(index, { images: fromLines(e.target.value) })}
                          />
                          <ImageUploadButton
                            disabled={!canPublish}
                            label="追加"
                            onUploaded={(path) =>
                              updateFloor(index, { images: [...(floor.images || []), path] })
                            }
                          />
                        </div>
                      </Form.Group>

                      <Form.Group>
                        <Form.Label>スペック（「面積：40㎡」の形で1行に1つ）</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={toLines((floor.specs || []).map((s) => `${s.label}：${s.value}`))}
                          onChange={(e) =>
                            updateFloor(index, {
                              specs: fromLines(e.target.value).map((line) => {
                                const [label, ...rest] = line.split('：');
                                return { label: (label || '').trim(), value: rest.join('：').trim() };
                              }),
                            })
                          }
                        />
                      </Form.Group>
                    </Card.Body>
                  </Card>
                ))}

                <Form.Group className="mb-3">
                  <Form.Label>設備・アメニティ（詳細JSON）</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={12}
                    value={amenitiesJson}
                    onChange={(e) => setAmenitiesJson(e.target.value)}
                    style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
                  />
                  <Form.Text muted>
                    設備一覧はレイアウト指定を含むため JSON で編集します。items の中身だけ書き換えるのが安全です。
                  </Form.Text>
                </Form.Group>

                <Button onClick={saveRooms} disabled={!canPublish || busy}>
                  更新して公開
                </Button>
              </Card.Body>
            </Card>
          )}
        </Tab>
      </Tabs>
    </Container>
  );
}

export default SitePosts;
